/**
 * Subdivision geometry for the states, provinces and territories mode — how it
 * reaches the globe, and how a tap on the globe becomes an answer.
 *
 * ===========================================================================
 * WHY THE POLYGONS ARE FETCHED AND NOT IMPORTED
 * ===========================================================================
 *
 * `us-states.json` is a quarter of a megabyte, and two of the three game modes
 * never draw a single state line. Imported statically it would sit in the entry
 * chunk that every player of every mode downloads before the home screen paints.
 * Behind a dynamic import it lands in its own chunk, fetched the first time a
 * subdivision match arms — exactly the trade `net/local.ts` and
 * `net/localassets.ts` already make, for exactly the same reason.
 *
 * It carries no answers. See the header of `@geogame/data/states`.
 */

import { useEffect, useState } from 'react';
import type { RegionCollection } from '@geogame/globe';
import type { LngLat } from '@geogame/shared';

/**
 * The camera each subdivision round opens on.
 *
 * Wide enough to hold Alaska, Hawaii and Puerto Rico as well as the contiguous
 * states, because all three are in the pool and two of them are in the
 * EXPLORER pool — a round that opens with its own answer off-screen spends the
 * player's clock on panning. Wide is the cost of that: at this framing Rhode
 * Island is a couple of pixels and has to be zoomed into, which is the same
 * deal the country mode gives Andorra.
 */
export const US_HOME_BOUNDS: readonly [number, number, number, number] = [-168, 17, -65, 66];

/**
 * Which dot a tap chose: the nearest one, always, with no cut-off.
 *
 * A radius would create dead zones, and a dead zone on a timed rung reads as a
 * dropped tap rather than as a rule — the player taps again, and the clock is
 * the thing that answers. The dots are the four major cities of ONE
 * subdivision and the camera is framed on that subdivision, so "nearest" is
 * never close: the shortest gap the bake permits between two of them is 10 km,
 * and most are a hundred or more.
 *
 * Distances are compared as squared degrees rather than as great circles. Over
 * a single state the difference cannot change which of four points is closest,
 * and this runs inside a tap handler.
 */
export function nearestMarker(
  markers: readonly LngLat[],
  at: LngLat,
): number | null {
  let best: number | null = null;
  let bestD = Number.POSITIVE_INFINITY;
  markers.forEach((m, i) => {
    // Longitude is compressed by latitude; without the cosine a tap between
    // two cities at 60 N is judged on a scale where east-west counts double.
    const scale = Math.cos((at[1] * Math.PI) / 180);
    const dx = (m[0] - at[0]) * scale;
    const dy = m[1] - at[1];
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

/**
 * The subdivision outlines, once they have arrived, or null.
 *
 * Fetched at most once per session and only when `enabled` — the hook is
 * mounted by the match screen in every mode, and the import must not fire for a
 * player who never chooses this one.
 */
let cached: RegionCollection | null = null;

export function useSubdivisionRegions(enabled: boolean): RegionCollection | null {
  const [regions, setRegions] = useState<RegionCollection | null>(cached);

  useEffect(() => {
    if (!enabled || cached) return undefined;
    let live = true;
    void import('@geogame/data/us-states.json')
      .then((mod) => {
        cached = (mod.default ?? mod) as unknown as RegionCollection;
        if (live) setRegions(cached);
      })
      .catch((err: unknown) => {
        // The globe stays in country mode, which means taps resolve to `USA`
        // and every pinpoint is wrong. Loud, because there is no degraded mode
        // worth having here.
        console.error('[regions] subdivision geometry failed to load', err);
      });
    return () => {
      live = false;
    };
  }, [enabled]);

  return enabled ? regions : null;
}
